import { AdminTable } from './shared/AdminDataGrid';
/** Reviewed questionnaire content; translations are versioned in the shared catalog. */
import { Show, SimpleShowLayout, useRecordContext } from 'react-admin';
import { Typography, Box, Card, CardContent, Chip, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert } from '@mui/material';
import { semantic } from '@rekindle/shared/theme';
import { LANGUAGE_LABELS } from '../constants';
interface QuestionOption {
  value: string | number;
  label: string;
}

interface QuestionTranslation {
  language: string;
  text: string;
  options: string[];
}

interface QuestionRecord {
  id: string;
  text: string;
  tier: number;
  askAt: string;
  type: string | null;
  contextField: string;
  multiSelect: number | null;
  options: QuestionOption[];
  // React Admin may first provide the cached list record, before getOne completes.
  translations?: QuestionTranslation[];
}

/**
 * Title for the show page
 */
function QuestionTitle() {
  const record = useRecordContext<QuestionRecord>();
  return <span>Question: {record?.id || ''}</span>;
}

/**
 * Question details card
 */
function QuestionDetails() {
  const record = useRecordContext<QuestionRecord>();
  if (!record) return null;

  const tierColor = record.tier === 1 ? semantic.info : semantic.warning;
  const tierLabel = record.tier === 1 ? 'Tier 1 (Onboarding)' : 'Tier 2 (Later)';

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Question Details
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">Question Text (English)</Typography>
          <Typography variant="body1">{record.text}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography variant="caption" color="textSecondary">Tier</Typography>
            <Box>
              <Chip label={tierLabel} size="small" sx={{ backgroundColor: tierColor, color: 'white' }} />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Ask At</Typography>
            <Box>
              <Chip label={record.askAt} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Type</Typography>
            <Typography variant="body2">{record.type || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Context Field</Typography>
            <Typography variant="body2">{record.contextField}</Typography>
          </Box>
          {record.multiSelect && (
            <Box>
              <Typography variant="caption" color="textSecondary">Multi-Select</Typography>
              <Typography variant="body2">Max {record.multiSelect} selections</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Options display card
 */
function OptionsCard() {
  const record = useRecordContext<QuestionRecord>();
  if (!record?.options?.length) return null;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Options (English - {record.options?.length || 0} total)
        </Typography>
        <TableContainer>
          <AdminTable size="small">
            <TableHead>
              <TableRow>
                <TableCell width={100}>Value</TableCell>
                <TableCell>Label</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(record.options || []).map((option, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Chip label={String(option.value)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{option.label}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </AdminTable>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

function TranslationsSection() {
  const record = useRecordContext<QuestionRecord>();
  if (!record) return null;
  if (!record.translations) return <Box role="status"><Typography>Loading translations…</Typography></Box>;
  return <Box>
    <Alert severity="info" sx={{ mb: 2 }}>These reviewed translations are versioned with the app. Changes belong in the shared questionnaire catalog so every language stays consistent.</Alert>
    {record.translations.map(translation => <Card key={translation.language} variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>{LANGUAGE_LABELS[translation.language] || translation.language}</Typography>
        <Typography variant="body1" gutterBottom>{translation.text}</Typography>
        {translation.options.map((label, index) => <Typography variant="body2" key={record.options[index]?.value ?? index}>{index + 1}. {label}</Typography>)}
      </CardContent>
    </Card>)}
  </Box>;
}

export function QuestionShow() {
  return (
    <Show title={<QuestionTitle />}>
      <SimpleShowLayout>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <QuestionDetails />
          <OptionsCard />
          <TranslationsSection />
        </Box>
      </SimpleShowLayout>
    </Show>
  );
}
